import React from 'react';
import {Col, Row, Typography, Layout, Avatar} from 'antd';
import styled, {keyframes} from 'styled-components';
import LaptopImage from '../images/laptop_mockup.png';
import PageImage from '../images/background-img.png';
import ImgOne from '../images/Desjah.jpg';
import ImgTwo from '../images/Daniel Fidanque.jpg';
import ImgThree from '../images/Owen christensen.jpeg';
import ImgFour from '../images/Miles Franklyn.jpeg';

const {Content} = Layout;
const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const LandingContainer = styled.div`
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
`;

const PortfolioSection = styled(Content)`
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const CreativesSection = styled(Content)`
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  background: ${props => props.theme.colors?.neutral?.[900] || '#171717'};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 70%, rgba(226, 39, 132, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const SkillsSection = styled(Content)`
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-top: ${props => props.theme.spacing?.[8] || '2rem'};
    margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
    animation: ${fadeInUp} 1s ease-out;

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
      margin-top: ${props => props.theme.spacing?.[6] || '1.5rem'};
    }
  }
`;

const SectionDescription = styled(Paragraph)`
  && {
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    animation: ${slideInLeft} 1s ease-out 0.2s both;
  }
`;

const ImageWrapper = styled.div`
  animation: ${slideInRight} 1s ease-out 0.4s both;

  img {
    width: 100%;
    max-width: 400px;
    height: auto;
    border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
    box-shadow: ${props =>
      props.theme.boxShadow?.xl ||
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
    transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const CreativeCard = styled.div`
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
  margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  animation: ${fadeInUp} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props =>
      props.theme.boxShadow?.xl ||
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
    border-color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    height: 180px;
    padding: ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const CreativeAvatar = styled(Avatar)`
  && {
    border: 3px solid ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    box-shadow: ${props =>
      props.theme.boxShadow?.lg ||
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'};
  }
`;

const CreativeName = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.xl || '1.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[1] || '0.25rem'};
  }
`;

const CreativeUniversity = styled.span`
  font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const creatives = [
  {
    name: 'DESJAH ALTVATER',
    university: 'Syracuse University',
    image: ImgOne,
    delay: '0.1s',
  },
  {
    name: 'DANIEL FIDANQUE',
    university: 'Boston University',
    image: ImgTwo,
    delay: '0.2s',
  },
  {
    name: 'OWEN CHRISTENSEN',
    university: 'Cal Poly Tech',
    image: ImgThree,
    delay: '0.3s',
  },
  {
    name: 'MILES FRANKLYN',
    university: 'Rutgers University',
    image: ImgFour,
    delay: '0.4s',
  },
];

export const LandingInfo = () => {
  return (
    <LandingContainer>
      <Layout>
        {/* Portfolio Section */}
        <PortfolioSection>
          <ContentWrapper>
            <Row gutter={[32, 32]} align="middle">
              <Col xs={24} lg={12}>
                <SectionTitle level={2}>
                  Create a Profile, and Share Your Interactive Portfolio
                </SectionTitle>
                <SectionDescription>
                  Our vision is to empower the next generation of creatives. We bridge the gap
                  between creatives and brands by providing a platform for college students to
                  develop their portfolio, showcase their skills, and increase their visibility,
                  while providing brands exclusive access to local Gen-Z consumers and Gen-Z content
                  creators.
                </SectionDescription>
              </Col>
              <Col xs={24} lg={12}>
                <ImageWrapper>
                  <img src={LaptopImage} alt="CLLCTVE Platform Preview" />
                </ImageWrapper>
              </Col>
            </Row>
          </ContentWrapper>
        </PortfolioSection>

        {/* Creatives Section */}
        <CreativesSection>
          <ContentWrapper>
            <SectionTitle level={2} style={{textAlign: 'center', marginBottom: '3rem'}}>
              Connect with Other Creatives
            </SectionTitle>
            <Row gutter={[24, 24]}>
              {creatives.map((creative, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <CreativeCard delay={creative.delay}>
                    <CreativeAvatar size={80} src={creative.image} />
                    <div style={{marginTop: '1rem', textAlign: 'center'}}>
                      <CreativeName level={4}>{creative.name}</CreativeName>
                      <CreativeUniversity>{creative.university}</CreativeUniversity>
                    </div>
                  </CreativeCard>
                </Col>
              ))}
            </Row>
          </ContentWrapper>
        </CreativesSection>

        {/* Skills Section */}
        <SkillsSection>
          <ContentWrapper>
            <Row gutter={[32, 32]} align="middle">
              <Col xs={24} lg={12} order={2}>
                <SectionTitle level={2}>Track Your Skills and Engage with Brands</SectionTitle>
                <SectionDescription>
                  As creatives post new content, update their skills, and engage with the platform,
                  they will increase their visibility to be seen by our brand partners looking for
                  fresh young talent to hire for summer internships, local campaigns, branded
                  content, and more.
                </SectionDescription>
              </Col>
              <Col xs={24} lg={12} order={1}>
                <ImageWrapper>
                  <img src={PageImage} alt="Skills Tracking Interface" />
                </ImageWrapper>
              </Col>
            </Row>
          </ContentWrapper>
        </SkillsSection>
      </Layout>
    </LandingContainer>
  );
};
